import { Users, CalendarDays, Briefcase, type LucideIcon } from 'lucide-react';

export type ScheduleKey = 'people_events' | 'probation_ending';

export interface CatalogItem {
  eventType: string; // MUST match NotificationPreference.eventType
  name: string;
  extraEventTypes?: string[]; // for rows that map to >1 pref key
  scheduleKey?: ScheduleKey; // present ⇒ has a company-wide schedule editor
}

export interface CatalogSection {
  label: string;
  icon: LucideIcon;
  items: CatalogItem[];
}

export const NOTIFICATION_CATALOG: CatalogSection[] = [
  {
    label: 'People',
    icon: Users,
    items: [
      // Birthdays + work anniversaries share the people_events schedule.
      // They are TWO preference keys — toggling channels writes both.
      {
        eventType: 'birthday_reminder',
        extraEventTypes: ['work_anniversary_reminder'],
        name: 'Birthdays & work anniversaries',
        scheduleKey: 'people_events',
      },
      {
        eventType: 'probation_ending_reminder',
        name: 'Probation ending',
        scheduleKey: 'probation_ending',
      },
      { eventType: 'probation_completed', name: 'Probation completed' },
    ],
  },
  {
    label: 'Absences',
    icon: CalendarDays,
    items: [
      { eventType: 'absence_request_submitted', name: 'Request submitted' },
      { eventType: 'absence_approval_pending', name: 'Awaiting your approval' },
      { eventType: 'absence_reminder_digest', name: 'Approval reminders' },
      { eventType: 'absence_request_approved', name: 'Request approved' },
      { eventType: 'absence_request_rejected', name: 'Request declined' },
      { eventType: 'absence_request_auto_approved', name: 'Auto-approved' },
      // Backend emits two distinct keys: recalled (submitter pulls back) and
      // cancelled (admin/manager cancels). Both map to this single UI row.
      {
        eventType: 'absence_request_cancelled',
        extraEventTypes: ['absence_request_recalled'],
        name: 'Cancelled or recalled',
      },
    ],
  },
  {
    label: 'Recruitment',
    icon: Briefcase,
    items: [
      { eventType: 'interview_scheduled', name: 'Interview scheduled' },
      { eventType: 'interview_rescheduled', name: 'Interview rescheduled or canceled' },
      // Note: interview_canceled is emitted when an interview is hard-deleted.
      // Window-proposed emails reuse interview_scheduled / interview_rescheduled pref keys.
      { eventType: 'interview_canceled', name: 'Interview canceled' },
      { eventType: 'candidate_note_mention', name: 'Candidate mention' },
      { eventType: 'candidate_converted', name: 'Candidate hired' },
    ],
  },
];

export const LEAD_DAY_OPTIONS = [0, 1, 2, 3, 5, 7, 10, 14] as const;
export const leadLabel = (d: number): string =>
  d === 0 ? 'On the day' : d === 7 ? '1 week' : d === 14 ? '2 weeks' : `${d} day${d === 1 ? '' : 's'}`;
